import type { Messages } from "@/i18n/locales/en";
import { legalMessagesPt } from "@/i18n/locales/legalPtData";
import { pagesMessagesPt } from "@/i18n/locales/pagesPtData";

export const ptMessages = {
  meta: {
    description:
      "WasOK — escaneie produtos, salve avaliações e anotações, lembre o que valeu a pena comprar.",
    title: "WasOK — Lembre o que valeu a pena comprar",
  },
  header: {
    navFeatures: "Recursos",
    navHowItWorks: "Como funciona",
    navPrivacy: "Privacidade",
    navUseCases: "Casos de uso",
    download: "Baixar",
    openMenu: "Abrir menu",
    closeMenu: "Fechar menu",
    languageAria: "Idioma",
  },
  hero: {
    badge: "📱 Já disponível no Android",
    title: "Lembre o que valeu a pena comprar.",
    subtitle:
      "Escaneie produtos, salve avaliações e anotações, e lembre na hora se um produto foi realmente bom.",
    googlePlay: "Baixar no Google Play",
    watchDemo: "Ver demo",
    privacyLine: "Todos os dados ficam no seu dispositivo.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Escaneie para lembrar",
    snackCard: "Delicioso!",
    coffeeCard: "Muito amargo",
    scrollHint: "Role para explorar",
  },
  problems: {
    heading: "Soa familiar?",
    subheading:
      "Todo mundo já passou por isso — na frente da prateleira tentando lembrar se gostou da última vez.",
    notedAgo: "✓ Você anotou isso há 3 meses",
    items: [
      {
        question: "Qual chocolate eu realmente gostei?",
        note: "Doçura perfeita",
      },
      {
        question: "Por que eu parei de comprar esse shampoo?",
        note: "Ressecou meu cabelo",
      },
      {
        question: "Esse café foi bom ou decepcionante?",
        note: "Muito amargo",
      },
    ],
  },
  howItWorks: {
    heading: "Como funciona",
    subheading:
      "Quatro passos simples para nunca mais esquecer suas opiniões sobre produtos.",
    stepPrefix: "Passo",
    steps: [
      {
        title: "Escaneie qualquer código de barras",
        description:
          "Escaneamento rápido e fácil com a câmera. Funciona com qualquer código de barras.",
      },
      {
        title: "Salve avaliações, comentários e fotos",
        description:
          "Avalie produtos, adicione anotações, anexe fotos e registre preços e locais.",
      },
      {
        title: "Lembre na hora da sua experiência",
        description:
          "Escaneie o mesmo produto depois e veja sua avaliação e anotações anteriores.",
      },
      {
        title: "Monte listas de compras inteligentes",
        description:
          "Crie listas de compras com produtos em que você já confia e gosta.",
      },
    ],
  },
  privacy: {
    heading: "Seu histórico de compras continua seu.",
    body: "O WasOK ajuda você a lembrar produtos que experimentou, mantendo avaliações, anotações, fotos e listas de compras armazenadas localmente no seu dispositivo.",
    trustBanner:
      "💚 Feito em torno do seu histórico de compras — não em torno de coletar seus dados.",
    bullets: [
      "Sem necessidade de conta",
      "Sem sincronização na nuvem obrigatória",
      "Armazenamento local primeiro",
      "Backups sob seu controle",
      "Consulta opcional de código de barras para informações do produto.",
      "Com anúncios via Google AdMob",
    ],
  },
  features: {
    heading: "Tudo o que você precisa",
    subheading:
      "Recursos poderosos pensados para tornar o rastreamento de produtos simples e privado.",
    items: [
      {
        title: "Leitor de código de barras",
        description: "Escaneamento rápido e preciso de código de barras para qualquer produto.",
      },
      {
        title: "Avaliações de produtos",
        description: "Avalie produtos de 1 a 5 estrelas para lembrar a qualidade.",
      },
      {
        title: "Fotos de produtos",
        description: "Anexe fotos para lembrar a embalagem ou detalhes.",
      },
      {
        title: "Categorias",
        description: "Organize produtos em categorias personalizadas.",
      },
      {
        title: "Rastreamento de lojas",
        description: "Lembre onde você comprou cada produto.",
      },
      {
        title: "Registro de preços",
        description: "Acompanhe preços para encontrar as melhores ofertas.",
      },
      {
        title: "Listas de compras",
        description: "Monte listas com seus produtos favoritos.",
      },
      {
        title: "Acesso offline",
        description: "Funciona perfeitamente sem conexão com a internet.",
      },
      {
        title: "Armazenamento local",
        description: "Todos os seus dados armazenados com segurança no seu dispositivo.",
      },
    ],
  },
  useCases: {
    heading: "Perfeito para cada corredor",
    subheading:
      "Seja para comida, beleza ou itens de casa — o WasOK ajuda você a lembrar.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks e guloseimas",
        description: "Lembre quais doces foram realmente bons.",
      },
      {
        emoji: "☕",
        title: "Café e bebidas",
        description: "Acompanhe cafés que valem a pena comprar de novo.",
      },
      {
        emoji: "💄",
        title: "Cosméticos e skincare",
        description: "Anote reações da pele.",
      },
      {
        emoji: "🧹",
        title: "Itens de casa",
        description: "Evite recomprar produtos que falharam.",
      },
    ],
  },
  screenshots: {
    heading: "Bonito e intuitivo",
    subheading:
      "Uma interface pensada com cuidado que torna o rastreamento de produtos um prazer.",
    items: [
      { title: "Scanner" },
      { title: "Detalhe do produto" },
      { title: "Inserção de avaliação" },
      { title: "Busca e histórico" },
      { title: "Lista de compras" },
    ],
  },
  cta: {
    heading: "Memória de compras para a vida real.",
    body: "Pare de esquecer quais produtos valeram a pena. Comece a comprar de forma mais inteligente hoje.",
    googlePlay: "Baixar no Google Play",
    scanQr: "Escaneie para baixar",
    tagline: "Privado · Offline · Simples",
  },
  footer: {
    tagline: "Lembre o que valeu a pena comprar.",
    linksHeading: "Links",
    privacyPolicy: "Política de Privacidade",
    terms: "Termos de Serviço",
    contact: "Contato",
    linkFaq: "FAQ",
    linkLicenses: "Licenças de código aberto",
    linkAdvertising: "Publicidade e monetização",
    downloadHeading: "Baixar",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Todos os direitos reservados. Feito com 💚 para compras mais inteligentes.",
  },
  legal: legalMessagesPt,
  faq: pagesMessagesPt.faq,
  support: pagesMessagesPt.support,
  advertising: pagesMessagesPt.advertising,
} satisfies Messages;
